import { Component, computed, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '@app/shared/services/auth.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { UrlSegment } from '@app/shared/types/enums/url-segment.enum';
import { UiAvatarComponent } from '@app/shared/ui/avatar/ui-avatar.component';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { BasicDataComponent } from '@app/views/user-profile/basic-data/basic-data-view.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, Observable, take } from 'rxjs';
import { UserProfileSettingsComponent } from './settings/user-profile-settings.component';

@Component({
  selector: 'proffeo-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.css',
  imports: [
    RouterLink,
    MatTooltip,
    UiAvatarComponent,
    UiPageWrapperComponent,
    BasicDataComponent,
    UserProfileSettingsComponent,
    TranslatePipe
  ]
})
export class UserProfileComponent {
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);
  private readonly queryParams = toSignal(this.route.queryParamMap, {
    initialValue: this.route.snapshot.queryParamMap
  });

  private readonly settings = viewChild(UserProfileSettingsComponent);
  protected readonly basicData = viewChild(BasicDataComponent);
  protected readonly user = this.authService.currentUser;
  protected readonly showDetails = computed(() => this.queryParams().get('section') === 'details');
  protected readonly showSettings = computed(() => this.queryParams().get('section') === 'settings');
  protected readonly initials = computed(() =>
    `${this.user()?.firstName?.slice(0, 1) ?? ''}${this.user()?.lastName?.slice(0, 1) ?? ''}`.toUpperCase()
  );
  protected readonly fullName = computed(() =>
    [this.user()?.firstName, this.user()?.lastName].filter(Boolean).join(' ')
  );
  protected readonly profileRoute = `/${UrlSegment.USER_PROFILE}`;
  protected readonly signOutRoute = `/${UrlSegment.SIGN_OUT}`;
  protected readonly reportsRoute = `/${UrlSegment.REPORTS}/${UrlSegment.WORK_TIME}`;
  // Sections without an existing domain contract stay unavailable.
  protected readonly sections = [
    { id: 'qualifications', icon: 'qualifications', key: 'qualifications' },
    { id: 'tools', icon: 'tools', key: 'tools' },
    { id: 'workwear', icon: 'workwear', key: 'workwear' },
    { id: 'files', icon: 'files', key: 'files' },
    { id: 'settings', icon: 'settings', key: 'settings' },
    { id: 'notifications', icon: 'notifications', key: 'notifications' }
  ];
  protected returnToProfile(): void {
    if (this.basicData()?.editMode()) return;
    void this.router.navigate([this.profileRoute]);
  }

  public confirmNavigation(): boolean | Observable<boolean> {
    const settings = this.settings();
    const basicData = this.basicData();
    if (settings?.saving() || basicData?.saving()) return false;
    const isDirty = (settings?.isDirty() ?? false) || (basicData?.hasUnsavedChanges() ?? false);
    if (!isDirty) return true;
    return this.dialogService
      .openConfirmationDialog({
        title: this.translate.instant('user-profile-settings.discard-title'),
        desc: this.translate.instant('user-profile-settings.discard-description')
      })
      .afterClosed()
      .pipe(
        take(1),
        map(confirmed => {
          if (confirmed) {
            settings?.discardChanges();
            basicData?.discardChanges();
          }
          return confirmed === true;
        })
      );
  }
}
